import { useEffect } from 'react';
import { Colors } from '@quizzach/styles';
import { QuizSlidesContainer } from '@quizzach/play-styles';
import {
	ApiFailedReason,
	QuizOutletContextType,
	QuestionType,
} from '@quizzach/interfaces';
import {
	AbcdQuestionDesktop,
	AbcdQuestionMobile,
	AssociationQuestionDesktop,
	AssociationQuestionMobile,
	BottomNavMobile,
	ConnectionQuestionDesktop,
	ConnectionQuestionMobile,
	EstimationQuestionDesktop,
	EstimationQuestionMobile,
	StandardQuestionDesktop,
	StandardQuestionMobile,
	VisualQuestionDesktop,
	VisualQuestionMobile,
} from '@quizzach/layouts';
import { scrollToTop } from '@quizzach/utils';
import { useOutletContext } from 'react-router-dom';
import { AlertMessage, BottomNav, FitToViewport } from '@quizzach/components';
import { useWindowListener } from '@quizzach/contexts';

// Question component map for mobile and desktop variants
const QUESTION_COMPONENTS = {
	[QuestionType.standard]: {
		Mobile: StandardQuestionMobile,
		Desktop: StandardQuestionDesktop,
	},
	[QuestionType.abcd]: {
		Mobile: AbcdQuestionMobile,
		Desktop: AbcdQuestionDesktop,
	},
	[QuestionType.association]: {
		Mobile: AssociationQuestionMobile,
		Desktop: AssociationQuestionDesktop,
	},
	[QuestionType.connection]: {
		Mobile: ConnectionQuestionMobile,
		Desktop: ConnectionQuestionDesktop,
	},
	[QuestionType.visual]: {
		Mobile: VisualQuestionMobile,
		Desktop: VisualQuestionDesktop,
	},
	[QuestionType.estimation]: {
		Mobile: EstimationQuestionMobile,
		Desktop: EstimationQuestionDesktop,
	},
} as const;

export const QuestionSlides = () => {
	const { quiz, currentQuestionNumber, handlePrevious, handleNext, error } =
		useOutletContext<QuizOutletContextType>();

	const { touchDevice } = useWindowListener();

	const currentQuestion = quiz?.questions[currentQuestionNumber - 1];
	const currentQuestionType = currentQuestion?.type;

	useEffect(() => {
		scrollToTop();
	}, [currentQuestionNumber]);

	if (!quiz) {
		return <></>;
	}

	// Helper function to render the appropriate question component
	const renderQuestionComponent = () => {
		if (!currentQuestionType || !QUESTION_COMPONENTS[currentQuestionType]) {
			return null;
		}

		const componentVariant = touchDevice ? 'Mobile' : 'Desktop';
		const QuestionComponent = QUESTION_COMPONENTS[currentQuestionType][
			componentVariant
		] as any;

		// The redesigned stage has no boxed question containers — text sits
		// directly on the framed-cover backdrop.
		const color = Colors.none;

		// Estimation questions have different prop structure
		if (currentQuestionType === QuestionType.estimation) {
			return (
				<QuestionComponent color={color} currentQuestion={currentQuestion} />
			);
		}

		// All other question types have navigation props
		return (
			<QuestionComponent
				color={color}
				currentQuestionNumber={currentQuestionNumber}
				currentQuestion={currentQuestion}
				totalQuestions={quiz?.questions?.length}
			/>
		);
	};

	return (
		<>
			{error && <AlertMessage code={ApiFailedReason.default} type='error' />}

			{!error && touchDevice && (
				<QuizSlidesContainer data-id='question-slides-container-02-mobile'>
					{/* Mobile scrolls naturally; the fit-to-viewport scaling is a
					    desktop/presentation concern. */}
					<div>{renderQuestionComponent()}</div>
					<BottomNavMobile
						question={currentQuestion}
						handleNext={handleNext}
						handlePrevious={handlePrevious}
						isPremadeOrEstimation={true}
						type='plain'
					/>
				</QuizSlidesContainer>
			)}
			{!error && !touchDevice && (
				<QuizSlidesContainer data-id='question-slides-container-02-desktop'>
					<FitToViewport>{renderQuestionComponent()}</FitToViewport>
					<BottomNav
						question={currentQuestion}
						handleNext={handleNext}
						handlePrevious={handlePrevious}
						isPremadeOrEstimation={true}
						type='plain'
					/>
				</QuizSlidesContainer>
			)}
		</>
	);
};
